import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Tabs, Table, Button, Select, Input, Form, message, Space, Tag, Popconfirm, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UserOutlined, ShopOutlined } from '@ant-design/icons';
import apiClient from '../utils/axiosConfig.ts';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface Customer {
  id: number;
  company: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface ProductCustomer {
  id: number;
  customer_id: number;
  is_active: boolean;
  notes?: string;
  customer?: Customer;
  created_at: string;
}

interface ProductSupplier {
  id: number;
  supplier_id: number;
  is_primary: boolean;
  supplier_product_code?: string;
  cost?: number;
  lead_time_days?: number;
  minimum_order_quantity: number;
  is_active: boolean;
  notes?: string;
  supplier?: Supplier;
  created_at: string;
}

interface ProductAssociationsModalProps {
  visible: boolean;
  onCancel: () => void;
  productId: number;
  productName: string;
}

const ProductAssociationsModal: React.FC<ProductAssociationsModalProps> = ({
  visible,
  onCancel,
  productId,
  productName
}) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [productCustomers, setProductCustomers] = useState<ProductCustomer[]>([]);
  const [productSuppliers, setProductSuppliers] = useState<ProductSupplier[]>([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<ProductCustomer | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<ProductSupplier | null>(null);
  const [customerForm] = Form.useForm();
  const [supplierForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Fetching data for product ID:', productId);
      const [customersRes, suppliersRes, productCustomersRes, productSuppliersRes] = await Promise.all([
        apiClient.get('/customers'),
        apiClient.get('/suppliers'),
        apiClient.get(`/products/${productId}/customers`),
        apiClient.get(`/products/${productId}/suppliers`)
      ]);

      setCustomers(customersRes.data);
      setSuppliers(suppliersRes.data);
      setProductCustomers(productCustomersRes.data);
      setProductSuppliers(productSuppliersRes.data);
      console.log('Data fetched successfully');
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (visible && productId) {
      fetchData();
    }
  }, [visible, productId, fetchData]);

  // Reset forms when modal closes
  useEffect(() => {
    if (!visible) {
      setShowCustomerForm(false);
      setShowSupplierForm(false);
      setEditingCustomer(null);
      setEditingSupplier(null);
      customerForm.resetFields();
      supplierForm.resetFields();
    }
  }, [visible, customerForm, supplierForm]);

  const handleAddCustomer = async (values: any) => {
    try {
      await apiClient.post(`/products/${productId}/customers`, {
        product_id: productId,
        customer_id: values.customer_id,
        is_active: values.is_active ?? true,
        notes: values.notes
      });
      message.success('Cliente associato con successo');
      setShowCustomerForm(false);
      customerForm.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'associazione del cliente');
    }
  };

  const handleAddSupplier = async (values: any) => {
    try {
      await apiClient.post(`/products/${productId}/suppliers`, {
        product_id: productId,
        supplier_id: values.supplier_id,
        is_primary: values.is_primary ?? false,
        supplier_product_code: values.supplier_product_code,
        cost: values.cost,
        lead_time_days: values.lead_time_days,
        minimum_order_quantity: values.minimum_order_quantity ?? 1,
        is_active: values.is_active ?? true,
        notes: values.notes
      });
      message.success('Fornitore associato con successo');
      setShowSupplierForm(false);
      supplierForm.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'associazione del fornitore');
    }
  };

  const handleUpdateCustomer = async (values: any) => {
    if (!editingCustomer) return;
    
    try {
      await apiClient.put(`/products/${productId}/customers/${editingCustomer.customer_id}`, {
        is_active: values.is_active,
        notes: values.notes
      });
      message.success('Associazione cliente aggiornata');
      setEditingCustomer(null);
      setShowCustomerForm(false);
      customerForm.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleUpdateSupplier = async (values: any) => {
    if (!editingSupplier) return;
    
    try {
      await apiClient.put(`/products/${productId}/suppliers/${editingSupplier.supplier_id}`, {
        is_primary: values.is_primary,
        supplier_product_code: values.supplier_product_code,
        cost: values.cost,
        lead_time_days: values.lead_time_days,
        minimum_order_quantity: values.minimum_order_quantity,
        is_active: values.is_active,
        notes: values.notes
      });
      message.success('Associazione fornitore aggiornata');
      setEditingSupplier(null);
      setShowSupplierForm(false);
      supplierForm.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDeleteCustomer = async (customerId: number) => {
    try {
      await apiClient.delete(`/products/${productId}/customers/${customerId}`);
      message.success('Associazione cliente rimossa');
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleDeleteSupplier = async (supplierId: number) => {
    try {
      await apiClient.delete(`/products/${productId}/suppliers/${supplierId}`);
      message.success('Associazione fornitore rimossa');
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nella rimozione');
    }
  };

  const handleEditCustomer = (customer: ProductCustomer) => {
    setEditingCustomer(customer);
    customerForm.setFieldsValue({
      is_active: customer.is_active,
      notes: customer.notes
    });
    setShowCustomerForm(true);
  };

  const handleEditSupplier = (supplier: ProductSupplier) => {
    setEditingSupplier(supplier);
    supplierForm.setFieldsValue({
      is_primary: supplier.is_primary,
      supplier_product_code: supplier.supplier_product_code,
      cost: supplier.cost,
      lead_time_days: supplier.lead_time_days,
      minimum_order_quantity: supplier.minimum_order_quantity,
      is_active: supplier.is_active,
      notes: supplier.notes
    });
    setShowSupplierForm(true);
  };

  const customerColumns = [
    {
      title: 'Cliente',
      dataIndex: ['customer', 'company'],
      key: 'customer',
      render: (company: string) => (
        <Space>
          <UserOutlined />
          <strong>{company}</strong>
        </Space>
      ),
    },
    {
      title: 'Stato',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Attivo' : 'Inattivo'}
        </Tag>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
    {
      title: 'Data Creazione',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record: ProductCustomer) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCustomer(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler rimuovere questa associazione?"
            onConfirm={() => handleDeleteCustomer(record.customer_id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const supplierColumns = [
    {
      title: 'Fornitore',
      dataIndex: ['supplier', 'name'],
      key: 'supplier',
      render: (name: string, record: ProductSupplier) => (
        <Space>
          <ShopOutlined />
          <strong>{name}</strong>
          {record.is_primary && <Tag color="gold">Primario</Tag>}
        </Space>
      ),
    },
    {
      title: 'Codice Prodotto',
      dataIndex: 'supplier_product_code',
      key: 'supplier_product_code',
      render: (code: string) => code || '-',
    },
    {
      title: 'Costo',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => cost ? `€ ${formatCurrency(cost)}` : '-',
    },
    {
      title: 'Tempo Consegna',
      dataIndex: 'lead_time_days',
      key: 'lead_time_days',
      render: (days: number) => days ? `${days} giorni` : '-',
    },
    {
      title: 'Qtà Min.',
      dataIndex: 'minimum_order_quantity',
      key: 'minimum_order_quantity',
    },
    {
      title: 'Stato',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Attivo' : 'Inattivo'}
        </Tag>
      ),
    },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record: ProductSupplier) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSupplier(record)}
          />
          <Popconfirm
            title="Sei sicuro di voler rimuovere questa associazione?"
            onConfirm={() => handleDeleteSupplier(record.supplier_id)}
            okText="Sì"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={`Associazioni Prodotto: ${productName}`}
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
    >
      <Tabs defaultActiveKey="customers">
        <TabPane tab="Clienti" key="customers">
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCustomer(null);
                customerForm.resetFields();
                setShowCustomerForm(true);
              }}
            >
              Associa Cliente
            </Button>
          </div>

          {showCustomerForm && (
            <div style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
              <Form
                form={customerForm}
                layout="vertical"
                onFinish={editingCustomer ? handleUpdateCustomer : handleAddCustomer}
              >
                {!editingCustomer && (
                  <Form.Item
                    name="customer_id"
                    label="Cliente"
                    rules={[{ required: true, message: 'Seleziona un cliente' }]}
                  >
                    <Select placeholder="Seleziona cliente" showSearch optionFilterProp="children">
                      {customers
                        .filter(customer => !productCustomers.some(pc => pc.customer_id === customer.id))
                        .map(customer => (
                          <Option key={customer.id} value={customer.id}>
                            {customer.company}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}

                <Form.Item name="is_active" label="Attivo" valuePropName="checked">
                  <Select defaultValue={true}>
                    <Option value={true}>Attivo</Option>
                    <Option value={false}>Inattivo</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="notes" label="Note">
                  <TextArea rows={2} placeholder="Note aggiuntive..." />
                </Form.Item>

                <Space>
                  <Button type="primary" htmlType="submit">
                    {editingCustomer ? 'Aggiorna' : 'Associa'}
                  </Button>
                  <Button onClick={() => {
                    setShowCustomerForm(false);
                    setEditingCustomer(null);
                    customerForm.resetFields();
                  }}>
                    Annulla
                  </Button>
                </Space>
              </Form>
            </div>
          )}

          <Table
            columns={customerColumns}
            dataSource={productCustomers}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>

        <TabPane tab="Fornitori" key="suppliers">
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingSupplier(null);
                supplierForm.resetFields();
                setShowSupplierForm(true);
              }}
            >
              Associa Fornitore
            </Button>
          </div>

          {showSupplierForm && (
            <div style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
              <Form
                form={supplierForm}
                layout="vertical"
                onFinish={editingSupplier ? handleUpdateSupplier : handleAddSupplier}
              >
                {!editingSupplier && (
                  <Form.Item
                    name="supplier_id"
                    label="Fornitore"
                    rules={[{ required: true, message: 'Seleziona un fornitore' }]}
                  >
                    <Select placeholder="Seleziona fornitore" showSearch optionFilterProp="children">
                      {suppliers
                        .filter(supplier => !productSuppliers.some(ps => ps.supplier_id === supplier.id))
                        .map(supplier => (
                          <Option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Form.Item name="is_primary" label="Fornitore Primario" valuePropName="checked">
                    <Select defaultValue={false}>
                      <Option value={true}>Sì</Option>
                      <Option value={false}>No</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="is_active" label="Attivo" valuePropName="checked">
                    <Select defaultValue={true}>
                      <Option value={true}>Attivo</Option>
                      <Option value={false}>Inattivo</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="supplier_product_code" label="Codice Prodotto Fornitore">
                    <Input placeholder="Codice del fornitore" />
                  </Form.Item>

                  <Form.Item name="cost" label="Costo (€)">
                    <InputNumber
                      min={0}
                      step={0.01}
                      precision={4}
                      formatter={inputNumberFormatter}
                      parser={inputNumberParser}
                      style={{ width: '100%' }}
                      placeholder="0,0000"
                    />
                  </Form.Item>

                  <Form.Item name="lead_time_days" label="Tempo di Consegna (giorni)">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="Giorni" />
                  </Form.Item>

                  <Form.Item name="minimum_order_quantity" label="Quantità Minima Ordine">
                    <InputNumber min={1} defaultValue={1} style={{ width: '100%' }} />
                  </Form.Item>
                </div>

                <Form.Item name="notes" label="Note">
                  <TextArea rows={2} placeholder="Note aggiuntive..." />
                </Form.Item>

                <Space>
                  <Button type="primary" htmlType="submit">
                    {editingSupplier ? 'Aggiorna' : 'Associa'}
                  </Button>
                  <Button onClick={() => {
                    setShowSupplierForm(false);
                    setEditingSupplier(null);
                    supplierForm.resetFields();
                  }}>
                    Annulla
                  </Button>
                </Space>
              </Form>
            </div>
          )}

          <Table
            columns={supplierColumns}
            dataSource={productSuppliers}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default ProductAssociationsModal;
